// src/pages/Dashboard/Notifications/NotificationList.tsx
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Mail, Send, BarChart3 } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import ConfigureNotification from "../../../components/ConfigureNotification/ConfigureNotification";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import "./NotificationList.css";

// Notification types from backend
export const NOTIFICATION_TYPES = {
  WELCOME: "WELCOME",
  PAYMENT_REMINDER: "PAYMENT_REMINDER",
  PAYMENT_OVERDUE: "PAYMENT_OVERDUE",
  CONTRACT_ENDING: "CONTRACT_ENDING",
  CONTRACT_EXPIRED: "CONTRACT_EXPIRED",
  ADHOC: "ADHOC",
} as const;

export type NotificationType = keyof typeof NOTIFICATION_TYPES;

// Human-readable labels for notification types
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  WELCOME: "Boas-vindas",
  PAYMENT_REMINDER: "Lembrete de Pagamento",
  PAYMENT_OVERDUE: "Pagamento em Atraso",
  CONTRACT_ENDING: "Contrato Encerrando",
  CONTRACT_EXPIRED: "Contrato Expirado",
  ADHOC: "Ad-hoc",
};

export type NotificationTemplate = {
  id?: string;
  coworkingId?: string;
  title?: string;
  description?: string | null;
  type?: NotificationType;
  imageUrl?: string | null;
  intro?: string | null;
  message?: string | null;
  conclusion?: string | null;
  contactLink?: string | null;
  daysOffset?: number;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type TemplateMetrics = {
  total: number;
  success: number;
  failed: number;
  lastSentAt: string | null;
};

type Props = {
  fetchLogs?: boolean;
  onEditTemplate: (tpl: NotificationTemplate) => void;
  onManualSend?: (tpl: NotificationTemplate) => void;
};

const NotificationList: React.FC<Props> = ({
  fetchLogs = true,
  onEditTemplate,
  onManualSend,
}) => {
  const [metrics, setMetrics] = useState<Record<string, TemplateMetrics>>({});
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const queryClient = useQueryClient();
  const modal = useGlobalModal();

  // React Query for templates
  const { data: templates = [], isLoading: loading, error: errorObj } = useQuery({
    queryKey: ['notificationTemplates'],
    queryFn: async () => {
      const resp = await api.get<NotificationTemplate[]>("/notifications/templates", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Handle various response formats
      let list: NotificationTemplate[] = [];
      const raw = resp as any;
      if (Array.isArray(raw)) list = raw;
      else if (raw?.data && Array.isArray(raw.data)) list = raw.data;
      else if (raw?.items && Array.isArray(raw.items)) list = raw.items;

      // Normalize data (extract from props wrapper if present)
      const normalized = list.map((t: any) => {
        const resolvedId = t?.id ?? t?._id ?? t?.props?.id ?? t?.props?._id ?? undefined;
        const maybeProps = t?.props ?? t;

        return {
          ...maybeProps,
          id: resolvedId,
          type: maybeProps?.type ?? t?.type,
          active: maybeProps?.active ?? t?.active ?? false,
        };
      });

      // Fetch metrics for each template
      if (fetchLogs && normalized.length > 0) {
        const metricsMap: Record<string, TemplateMetrics> = {};
        await Promise.all(
          normalized.map(async (t) => {
            if (!t.id) return;
            try {
              const metricsResp = await api.get<TemplateMetrics>(
                `/notifications/templates/${t.id}/metrics`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const data = (metricsResp as any)?.data ?? metricsResp;
              metricsMap[t.id] = data;
            } catch {
              metricsMap[t.id!] = { total: 0, success: 0, failed: 0, lastSentAt: null };
            }
          })
        );
        setMetrics(metricsMap);
      }

      return normalized;
    },
    enabled: !!token,
  });

  const error = errorObj ? ((errorObj as any)?.response?.data?.message ?? (errorObj as Error)?.message ?? "Erro ao carregar templates") : null;

  // Toggle template active status
  async function handleToggleTemplate(tpl: NotificationTemplate, next: boolean) {
    const id = tpl.id;

    if (!id) {
      console.error("Template sem ID:", tpl);
      modal.show({
        type: "error",
        title: "Erro ao atualizar",
        message: "Template sem ID - verifique o console para detalhes.",
        autoClose: 3500,
      });
      return;
    }
    if (!token) {
      modal.show({
        type: "error",
        title: "Sessão expirada",
        message: "Usuário não autenticado. Faça login novamente.",
        autoClose: 3500,
      });
      return;
    }

    const payload = {
      active: next,
    };

    try {
      await api.put(`/notifications/templates/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      queryClient.invalidateQueries({ queryKey: ['notificationTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
    } catch (err: any) {
      console.error("Erro ao atualizar template:", err);
      modal.show({
        type: "error",
        title: "Erro ao atualizar",
        message: err?.response?.data?.message ?? "Erro ao atualizar template.",
        autoClose: 4000,
      });
    }
  }

  // Get icon based on type
  function getIconForType(type?: string) {
    switch (type) {
      case "PAYMENT_REMINDER":
      case "PAYMENT_OVERDUE":
        return Mail;
      default:
        return Bell;
    }
  }

  // Get badge color based on type
  function getBadgeColor(type?: string): string {
    switch (type) {
      case "WELCOME":
        return "#22c55e";
      case "PAYMENT_REMINDER":
        return "#f59e0b";
      case "PAYMENT_OVERDUE":
        return "#ef4444";
      case "CONTRACT_ENDING":
        return "#3b82f6";
      case "CONTRACT_EXPIRED":
        return "#6b7280";
      case "ADHOC":
        return "#8b5cf6";
      default:
        return "#9333EA";
    }
  }

  if (loading) return <div className="notif-loading">Carregando notificações...</div>;
  if (error) return <div className="notif-error">Erro: {error}</div>;
  if (!templates || templates.length === 0)
    return <div className="notif-empty">Nenhuma notificação encontrada. Crie sua primeira!</div>;

  return (
    <div className="notif-grid">
      {templates.map((t) => {
        const Icon = getIconForType(t.type);
        const id = t.id ?? Math.random().toString(36).slice(2, 8);
        const enabled = !!t.active;
        const typeLabel = t.type ? NOTIFICATION_TYPE_LABELS[t.type as NotificationType] ?? t.type : "Desconhecido";
        const badgeColor = getBadgeColor(t.type);
        const templateMetrics = t.id ? metrics[t.id] : undefined;

        return (
          <div key={id} className="notif-card-wrapper">
            {/* Type badge */}
            <div className="notif-type-badge" style={{ backgroundColor: badgeColor }}>
              {typeLabel}
            </div>

            <ConfigureNotification
              title={t.title ?? "Sem título"}
              description={t.description ?? ""}
              Icon={Icon}
              bgColor="#F3F4F6"
              textColor="#111827"
              iconColor={enabled ? badgeColor : "#6B7280"}
              enabled={enabled}
              onToogle={(next) => handleToggleTemplate(t, next)}
              onEdit={() => {
                if (typeof onEditTemplate === "function") onEditTemplate(t);
              }}
            />

            {/* Metrics row */}
            {templateMetrics && (
              <div className="notif-metrics">
                <span className="notif-metric">
                  <BarChart3 size={14} />
                  {templateMetrics.total} enviados
                </span>
                <span className="notif-metric success">
                  {templateMetrics.success} ✓
                </span>
                <span className="notif-metric failed">
                  {templateMetrics.failed} ✗
                </span>
              </div>
            )}

            {/* Actions row */}
            <div className="notif-actions">
              {onManualSend && (
                <button
                  className="notif-action-btn"
                  onClick={() => onManualSend(t)}
                  title="Enviar agora"
                >
                  <Send size={14} />
                  Enviar
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationList;
